import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail, MessageSquare, Save, Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';

const Field = ({ label, value, onChange, type = 'text', placeholder, testId, disabled }) => (
  <div style={{ flex: 1, minWidth: 180 }}>
    <label className="pc-field">{label}</label>
    <input className="pc-input" type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} data-testid={testId} disabled={disabled} />
  </div>
);

const Toggle = ({ label, checked, onChange, testId }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.8rem', cursor: 'pointer' }}>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
    {label}
  </label>
);

/* ── SMS Card ───────────────────────────── */
const SmsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    api.get('/platform/settings/sms').then((r) => setCfg(r.data)).catch(() => toast.error('Could not load SMS config'));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...cfg };
      if (payload.api_key === '********') delete payload.api_key;
      const { data } = await api.put('/platform/settings/sms', payload);
      setCfg(data);
      toast.success('SMS settings saved');
    } catch { toast.error('Could not save SMS settings'); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div className="pc-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>;

  return (
    <div className="pc-card" data-testid="pc-sms-settings">
      <div className="pc-card-h">
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <MessageSquare className="h-3.5 w-3.5" style={{ color: '#15a6a6' }} />
          <h2>Platform SMS (Arkesel)</h2>
        </div>
        <button className="pc-btn pc-btn-primary" disabled={busy} onClick={save} data-testid="pc-sms-save">
          {busy ? 'Saving…' : <><Save className="h-3 w-3" /> Save</>}
        </button>
      </div>
      <div style={{ padding: '.9rem', display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
        <Toggle label="SMS enabled (platform-level)" checked={cfg.sms_enabled} onChange={(v) => setCfg((c) => ({ ...c, sms_enabled: v }))} testId="pc-sms-enabled" />
        <div className="pc-row">
          <div style={{ flex: 1, minWidth: 180 }}>
            <label className="pc-field">API key</label>
            <div style={{ display: 'flex', gap: '.3rem' }}>
              <input className="pc-input" type={showKey ? 'text' : 'password'} value={cfg.api_key || ''} onChange={(e) => setCfg((c) => ({ ...c, api_key: e.target.value }))}
                placeholder="ark_xxxxxxxx" data-testid="pc-sms-api-key" style={{ flex: 1 }} />
              <button type="button" className="pc-btn" onClick={() => setShowKey((v) => !v)}>
                {showKey ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              </button>
            </div>
          </div>
          <Field label="Sender ID" value={cfg.sender_id} onChange={(v) => setCfg((c) => ({ ...c, sender_id: v }))} placeholder="DutiLyne" testId="pc-sms-sender-id" />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem 1.5rem' }}>
          <Toggle label="Low-stock SMS alerts" checked={cfg.low_stock_alerts_sms} onChange={(v) => setCfg((c) => ({ ...c, low_stock_alerts_sms: v }))} testId="pc-sms-low-stock" />
          <Toggle label="Expiry SMS alerts" checked={cfg.expiry_alerts_sms} onChange={(v) => setCfg((c) => ({ ...c, expiry_alerts_sms: v }))} testId="pc-sms-expiry" />
          <Toggle label="Receipt SMS by default" checked={cfg.receipt_sms_default} onChange={(v) => setCfg((c) => ({ ...c, receipt_sms_default: v }))} testId="pc-sms-receipt" />
          <Toggle label="Marketing SMS enabled" checked={cfg.marketing_enabled} onChange={(v) => setCfg((c) => ({ ...c, marketing_enabled: v }))} testId="pc-sms-marketing" />
        </div>
      </div>
    </div>
  );
};

/* ── SMTP Card ──────────────────────────── */
const SmtpCard = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    api.get('/platform/settings/smtp').then((r) => setCfg(r.data)).catch(() => toast.error('Could not load SMTP config'));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...cfg };
      if (payload.smtp_password === '********') delete payload.smtp_password;
      const { data } = await api.put('/platform/settings/smtp', payload);
      setCfg(data);
      toast.success('SMTP settings saved');
    } catch { toast.error('Could not save SMTP settings'); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div className="pc-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>;

  return (
    <div className="pc-card" style={{ marginTop: '1rem' }} data-testid="pc-smtp-settings">
      <div className="pc-card-h">
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <Mail className="h-3.5 w-3.5" style={{ color: '#15a6a6' }} />
          <h2>Platform SMTP (Email)</h2>
        </div>
        <button className="pc-btn pc-btn-primary" disabled={busy} onClick={save} data-testid="pc-smtp-save">
          {busy ? 'Saving…' : <><Save className="h-3 w-3" /> Save</>}
        </button>
      </div>
      <div style={{ padding: '.9rem', display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
        <Toggle label="SMTP enabled" checked={cfg.smtp_enabled} onChange={(v) => setCfg((c) => ({ ...c, smtp_enabled: v }))} testId="pc-smtp-enabled" />
        <div className="pc-row">
          <Field label="SMTP host" value={cfg.smtp_host} onChange={(v) => setCfg((c) => ({ ...c, smtp_host: v }))} placeholder="smtp.gmail.com" testId="pc-smtp-host" />
          <Field label="Port" value={cfg.smtp_port} onChange={(v) => setCfg((c) => ({ ...c, smtp_port: v }))} type="number" placeholder="587" testId="pc-smtp-port" />
          <div style={{ flex: 1, minWidth: 120 }}>
            <label className="pc-field">Security</label>
            <select className="pc-input" value={cfg.smtp_security || 'starttls'} onChange={(e) => setCfg((c) => ({ ...c, smtp_security: e.target.value }))} data-testid="pc-smtp-security">
              <option value="starttls">STARTTLS</option>
              <option value="ssl">SSL/TLS</option>
              <option value="none">None</option>
            </select>
          </div>
        </div>
        <div className="pc-row">
          <Field label="Username" value={cfg.smtp_username} onChange={(v) => setCfg((c) => ({ ...c, smtp_username: v }))} placeholder="user@domain.com" testId="pc-smtp-username" />
          <div style={{ flex: 1, minWidth: 180 }}>
            <label className="pc-field">Password</label>
            <div style={{ display: 'flex', gap: '.3rem' }}>
              <input className="pc-input" type={showPw ? 'text' : 'password'} value={cfg.smtp_password || ''} onChange={(e) => setCfg((c) => ({ ...c, smtp_password: e.target.value }))}
                placeholder="••••••••" data-testid="pc-smtp-password" style={{ flex: 1 }} />
              <button type="button" className="pc-btn" onClick={() => setShowPw((v) => !v)}>
                {showPw ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              </button>
            </div>
          </div>
        </div>
        <div className="pc-row">
          <Field label="From email" value={cfg.smtp_from_email} onChange={(v) => setCfg((c) => ({ ...c, smtp_from_email: v }))} placeholder="alerts@dutilyne.com" testId="pc-smtp-from-email" />
          <Field label="From name" value={cfg.smtp_from_name} onChange={(v) => setCfg((c) => ({ ...c, smtp_from_name: v }))} placeholder="DutiLyne Alerts" testId="pc-smtp-from-name" />
        </div>
      </div>
    </div>
  );
};

export const PlatformMessaging = () => (
  <div data-testid="pc-messaging">
    <h1 className="pc-h1">SMS & Email Settings</h1>
    <p className="pc-sub" style={{ marginBottom: '1.1rem' }}>
      Platform-level messaging configuration. These credentials are used as the
      fallback for pharmacies that haven't set up their own accounts.
    </p>
    <SmsCard />
    <SmtpCard />
  </div>
);
